import type { IWidget, IWidgetResourceMethod } from "../widget-types/types";
import type { IDataFetcher, IDataFetcherFactory } from "./IDataFetcher";
import type { IWidgetResource } from "app/common/resources/widgetResource";
import widgetResource from "app/common/resources/widgetResource";
import { checkResultWithFullResponse } from "app/services/fetchService";

export const fetchWithFullResponseConfig = {
    withFullResponse: true,
};

export const fetchWithFullResponseInterceptorHandlers = [
    // on success - return only the result data
    (resultObj) => (checkResultWithFullResponse(resultObj) ? resultObj.result : resultObj),
    // on failure - align some return properties with older $resource
    (errorObj) => {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw checkResultWithFullResponse(errorObj)
            ? {
                  error: errorObj.error,
                  data: errorObj.error,
                  status: errorObj.response.status,
                  statusText: errorObj.response.statusText,
                  headers: (headerName) => errorObj.response.headers.get(headerName),
                  response: errorObj.response,
              }
            : {
                  error: errorObj,
                  data: errorObj,
                  status: null,
                  statusText: null,
                  headers: () => null,
                  response: null,
              };
    },
];

export class DefaultDataFetcher<T> implements IDataFetcher<T> {
    protected widget: IWidget<T>;
    protected widgetResource: IWidgetResource;

    constructor(widget: IWidget<T>) {
        this.widgetResource = widgetResource;
        this.widget = widget;
    }

    public fetch(params) {
        if (!params) {
            params = { ...this.widget.apiParams };
        }
        const { metric, ...rest } = params;
        const res = this.getResource();
        return res(rest).then(...fetchWithFullResponseInterceptorHandlers);
    }

    public destroy(): void {}

    protected getResource(config?): IWidgetResourceMethod {
        const widgetConfig = this.widget.getWidgetConfig();
        const widgetMetricConfig = this.widget.getWidgetMetricConfig();
        const resourceConfig = {
            ...fetchWithFullResponseConfig,
            ...config,
        };
        let apiController = widgetConfig.properties.apiController || widgetMetricConfig.apiController;
        // for cases in which the 'apiController' is dynamic
        apiController =
            typeof apiController === "function"
                ? apiController(this.widget.getWidgetModel().key, widgetConfig.properties)
                : apiController;
        const widgetResourceType = this.widget.getClass().getWidgetResourceType() || widgetConfig.properties.type;
        const apiMetric = this.widget.apiParams.metric || widgetConfig.properties.metric;
        if (apiController) {
            return this.widgetResource.resourceByController(apiController, apiMetric, resourceConfig)[
                widgetResourceType
            ];
        } else if (widgetMetricConfig.endpoint) {
            const endpoint =
                typeof widgetMetricConfig.endpoint === "function"
                    ? widgetMetricConfig.endpoint(widgetResourceType, widgetConfig.properties)
                    : widgetMetricConfig.endpoint;
            return this.widgetResource.resourceByEndpoint(endpoint, resourceConfig)[widgetResourceType];
        } else {
            // Deprecated : should currently be in use only for dashboard widgets
            // this._Sentry.fatal('Add metric apiController', widgetConfig);
            return this.widgetResource.websiteAppsResource(apiMetric, resourceConfig)[widgetResourceType];
        }
    }
}

const DefaultDataFetcherFactory: IDataFetcherFactory<any> = {
    create: function (widget) {
        return new DefaultDataFetcher<any>(widget);
    },
};

export default DefaultDataFetcherFactory;
